import { ProductFeatureIconName } from '@oy/uiicon';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AutoReportContext } from 'src/context';
import { useTranslate, useUserRole } from 'src/hooks';
import { ContentType } from 'src/pages/Settings/Notification/constants';
import { ShortcutDisbursementProducts, ShortcutMenu } from './constant';

export const useShortcutSideModalImpl = ({
  showVendorMaintenance,
  productName,
  handleToggleSideModal,
  handleAutoReportModal,
  handleFaqModal,
}: {
  showVendorMaintenance: boolean;
  productName: ShortcutDisbursementProducts;
  handleToggleSideModal: () => void;
  handleAutoReportModal: () => void;
  handleFaqModal: () => void;
}) => {
  const { onShowSideModal } = useContext(AutoReportContext);
  const { getTranslation } = useTranslate();
  const navigate = useNavigate();
  const { isSuperAdmin, isAdmin } = useUserRole();

  const faqDescription = (productName: ShortcutDisbursementProducts) => {
    switch (productName) {
      case ShortcutDisbursementProducts.BULK_DISBURSEMENT:
        return getTranslation('faq_bulkDisbursement_description');
      case ShortcutDisbursementProducts.API_DISBURSE:
        return getTranslation('faq_apiDisbursement_description');

      default:
        return '';
    }
  };

  const shortcutMenu: ShortcutMenu[] = [
    {
      title: getTranslation('pageTitle_bulkReceiptSettings'),
      description: getTranslation('settings_customReceipt_description'),
      iconName: 'bulk-receipt' satisfies ProductFeatureIconName,
      onClick: () => {
        navigate('/settings/bulk-receipt');
      },
      isFeatureIcon: true,
      showVisibility: [
        ShortcutDisbursementProducts.BULK_DISBURSEMENT,
        ShortcutDisbursementProducts.API_DISBURSE,
      ],
      havePermission: true,
    },
    {
      title: getTranslation('pageTitle_faq'),
      description: faqDescription(productName),
      iconName: 'help',
      onClick: () => {
        handleFaqModal();
      },
      isFeatureIcon: false,
      showVisibility: [
        ShortcutDisbursementProducts.BULK_DISBURSEMENT,
        ShortcutDisbursementProducts.API_DISBURSE,
      ],
      havePermission: true,
    },
    ...(showVendorMaintenance
      ? [
          {
            title: getTranslation('common_bankMaintenanceSchedule'),
            description: getTranslation('bankMaintenance_description'),
            iconName: 'bank-account' satisfies ProductFeatureIconName,
            onClick: () => {
              handleToggleSideModal();
            },
            isFeatureIcon: true,
            showVisibility: [
              ShortcutDisbursementProducts.BULK_DISBURSEMENT,
              ShortcutDisbursementProducts.API_DISBURSE,
              ShortcutDisbursementProducts.CLAIM_FUND,
            ],
            havePermission: true,
          },
        ]
      : []),
    {
      title: getTranslation('txCategory_title'),
      description: getTranslation('settings_transactionCategory_description'),
      iconName: 'transaction-category' satisfies ProductFeatureIconName,
      onClick: () => {
        navigate('/settings/transaction-category');
      },
      isFeatureIcon: true,
      showVisibility: [ShortcutDisbursementProducts.BULK_DISBURSEMENT],
      havePermission: isSuperAdmin || isAdmin,
    },
    {
      title: getTranslation('common_autoReport'),
      description: getTranslation('settings_autoReport_description'),
      iconName: 'bridging-report' satisfies ProductFeatureIconName,
      onClick: () => {
        handleAutoReportModal();
        onShowSideModal(productName, true);
      },
      isFeatureIcon: true,
      showVisibility: [
        ShortcutDisbursementProducts.BULK_DISBURSEMENT,
        ShortcutDisbursementProducts.API_DISBURSE,
        ShortcutDisbursementProducts.CLAIM_FUND,
        ShortcutDisbursementProducts.INTERNATIONAL_TRANSFER,
      ],
      havePermission: isSuperAdmin || isAdmin,
    },
    {
      title: getTranslation('pageTitle_multiApproval'),
      description: getTranslation('settings_multiApproval_description'),
      iconName: 'multi-entity-2' satisfies ProductFeatureIconName,
      onClick: () => {
        if (
          productName === ShortcutDisbursementProducts.INTERNATIONAL_TRANSFER
        ) {
          navigate('/settings/multi-approval?initTab=international_transfer');
        } else {
          navigate('/settings/multi-approval');
        }
      },
      isFeatureIcon: true,
      showVisibility: [
        ShortcutDisbursementProducts.BULK_DISBURSEMENT,
        ShortcutDisbursementProducts.INTERNATIONAL_TRANSFER,
      ],
      havePermission: isSuperAdmin || isAdmin,
    },
    {
      title: getTranslation('common_notification'),
      description: getTranslation('settings_notifications_description'),
      iconName: 'notifications-2' satisfies ProductFeatureIconName,
      onClick: () => {
        const data = { initTab: ContentType.SENDER_DISBURSEMENT };
        const query = new URLSearchParams(data).toString();
        navigate(`/settings/notification?${query}`);
      },
      isFeatureIcon: true,
      showVisibility: [
        ShortcutDisbursementProducts.BULK_DISBURSEMENT,
        ShortcutDisbursementProducts.API_DISBURSE,
      ],
      havePermission: isSuperAdmin || isAdmin,
    },
  ];

  const sortedShortcutMenu = [...shortcutMenu].sort((a, b) =>
    a.title.localeCompare(b.title),
  );

  const shortcutTitle = (productName: ShortcutDisbursementProducts) => {
    switch (productName) {
      case ShortcutDisbursementProducts.BULK_DISBURSEMENT:
        return getTranslation('helpConfiguration_title_bulkDisbursement');
      case ShortcutDisbursementProducts.API_DISBURSE:
        return getTranslation('helpConfiguration_title_apiDisbursement');
      case ShortcutDisbursementProducts.CLAIM_FUND:
        return getTranslation('helpConfiguration_title_claimFund');
      case ShortcutDisbursementProducts.INTERNATIONAL_TRANSFER:
        return getTranslation('helpConfiguration_title_internationalTransfer');
      default:
        return '';
    }
  };

  return { shortcutMenu: sortedShortcutMenu, shortcutTitle };
};
